// A linha de filtros do Pipeline (vista · ordem dos cartões, no kanban · status ·
// vendedor · cliente · busca · Novo Negócio). Os chips de etapa que moravam aqui viraram o funil desenhado
// (FunilEtapas.tsx, 01/10/2026). O botão "Novo briefing" saiu em 04/10/2026, a
// pedido do Lucas; o Dwight continua abrindo pelo próprio botão dele.
//
// Extraída de ComercialPipeline.tsx em 30/09/2026, junto do pedido do Lucas de
// filtrar por cliente: a página é um "entry fino" por decisão de 2026-05-23 (o
// kanban e os formulários já moram aqui em `./comercial-pipeline/`), e separar a
// barra permite montá-la num harness com os dados REAIS de produção — o jest do
// CRA5 não carrega o antd v6, então a única forma de conferir o dropdown de 186
// clientes é no navegador.
//
// A régua de quem passa pelo filtro NÃO está aqui: mora em `filtroCliente.ts`
// (pura, testada no CI) e no `passaFiltros` da página, que é um só para as três
// vistas e para a contagem dos chips.
import { Button, Input, Select, Typography, Radio, Flex, Tooltip } from 'antd';
import {
  PlusOutlined, SearchOutlined, AppstoreOutlined, UnorderedListOutlined, SortAscendingOutlined,
} from '@ant-design/icons';
import { STATUS_LABELS } from './utils';
import { buscaCasaCliente, type OpcaoCliente } from './filtroCliente';
import type { Ordenacao } from './saudeNegocio';

const { Text } = Typography;

export type Vista = 'lista' | 'kanban' | 'calendario';

export function FiltrosPipeline({
  vista, setVista,
  filtroStatus, setFiltroStatus,
  filtroResponsavel, setFiltroResponsavel,
  filtroCliente, setFiltroCliente,
  busca, setBusca,
  responsaveis, opcoesCliente,
  qtdFiltrada, qtdTotal, temFiltro, onLimpar,
  ordenacao, setOrdenacao,
  onNovoNegocio,
}: {
  vista: Vista;
  setVista: (v: Vista) => void;
  filtroStatus: string;
  setFiltroStatus: (v: string) => void;
  filtroResponsavel: string;
  setFiltroResponsavel: (v: string) => void;
  filtroCliente: string;
  setFiltroCliente: (v: string) => void;
  busca: string;
  setBusca: (v: string) => void;
  responsaveis: string[];
  opcoesCliente: OpcaoCliente[];
  qtdFiltrada: number;
  qtdTotal: number;
  temFiltro: boolean;
  onLimpar: () => void;
  ordenacao: Ordenacao;
  setOrdenacao: (o: Ordenacao) => void;
  onNovoNegocio: () => void;
}) {
  return (
    <>
      {/* UMA linha só em tela de computador. A base (flex-basis) de cada campo é
          o PISO dele: é a base que decide se a linha quebra, e o que sobra é
          repartido depois (os campos crescem até o teto). O pior caso — kanban,
          com a ordem dos cartões e um filtro ligado ("N/M · Limpar") — soma
          ~1.026 px e cabe num notebook de 1366 com o menu lateral aberto
          (1.039 px úteis; medido). Abaixo disso a linha quebra, como antes. */}
      <Flex gap={8} align="center" wrap="wrap" style={{ marginBottom: 10 }}>
        <Radio.Group value={vista} onChange={(e) => setVista(e.target.value)} size="small" style={{ flex: 'none' }}>
          <Radio.Button value="lista"><UnorderedListOutlined /> Lista</Radio.Button>
          <Radio.Button value="kanban"><AppstoreOutlined /> Kanban</Radio.Button>
          <Radio.Button value="calendario">📅 Calendário</Radio.Button>
        </Radio.Group>
        {/* A ordem dos cartões é um controle da VISTA (só existe no kanban), por
            isso fica colada no seletor de vista — e não na linha de resumo, onde
            empurrava o resultado do ano para uma segunda linha. */}
        {vista === 'kanban' && (
          <Tooltip title="Ordem dos cartões dentro de cada coluna">
            <Select value={ordenacao} onChange={(v) => setOrdenacao(v)}
              size="small" showSearch optionFilterProp="label" aria-label="Ordem dos cartões"
              prefix={<SortAscendingOutlined style={{ color: '#8A8F9C' }} />}
              style={{ flex: 'none', width: 120 }}
              options={[
                { value: 'valor', label: 'Maior valor' },
                { value: 'parados', label: 'Parados' },
                { value: 'recentes', label: 'Recentes' },
              ]} />
          </Tooltip>
        )}
        <Select value={filtroStatus || undefined} onChange={(v) => setFiltroStatus(v || '')}
          placeholder="Todos status" allowClear showSearch optionFilterProp="label"
          size="small" style={{ flex: '1 1 100px', minWidth: 100, maxWidth: 160 }}
          options={Object.entries(STATUS_LABELS).map(([k, v]) => ({ label: v, value: k }))} />
        <Select value={filtroResponsavel || undefined} onChange={(v) => setFiltroResponsavel(v || '')}
          placeholder="Todos vendedores" allowClear showSearch optionFilterProp="label"
          size="small" style={{ flex: '1 1 100px', minWidth: 100, maxWidth: 160 }}
          options={responsaveis.map(r => ({ label: r, value: r }))} />
        {/* Regra da casa: todo dropdown filtra pelo que se escreve. Aqui a busca
            casa razão social, nome fantasia e CNPJ por dígitos — por isso o
            `filterOption` próprio em vez do `optionFilterProp` do antd, que só
            olharia o rótulo. */}
        <Select value={filtroCliente || undefined} onChange={(v) => setFiltroCliente(v || '')}
          placeholder={opcoesCliente.length ? `Todos clientes (${opcoesCliente.length})` : 'Todos clientes'}
          allowClear showSearch size="small" style={{ flex: '1 1 110px', minWidth: 110, maxWidth: 200 }}
          // O dropdown é mais largo que o campo fechado: o rótulo tem nome +
          // fantasia + contagem, e o campo fechado cortaria a contagem justamente
          // de quem tem nome longo.
          popupMatchSelectWidth={false}
          styles={{ popup: { root: { minWidth: 380, maxWidth: 'min(92vw, 640px)' } } }}
          filterOption={(input, opt) => buscaCasaCliente(input, opt as any)}
          // Nome e contagem em colunas: o nome encolhe com "…" (e o nome
          // completo fica no title), a contagem NUNCA some. Medido em 30/09:
          // 8 dos 187 nomes passam da largura do dropdown, e o "…" comia
          // justamente o numero de negocios, que e o que se quer ver.
          optionRender={(opt) => {
            const o = opt.data as unknown as OpcaoCliente;
            return (
              <span title={o.title} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.nome}
                </span>
                <span style={{ color: '#8A8F9C', fontVariantNumeric: 'tabular-nums' }}>{o.negocios}</span>
              </span>
            );
          }}
          options={opcoesCliente} />
        <Input placeholder="Buscar título, cliente, área, vendedor..." value={busca}
          onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small" allowClear
          style={{ flex: '2 1 96px', minWidth: 96, maxWidth: 360 }} />
        {temFiltro && (
          <>
            <Text type="secondary" style={{ fontSize: 11, flex: 'none' }}>{qtdFiltrada}/{qtdTotal}</Text>
            <Button size="small" type="link" style={{ padding: 0, height: 'auto', flex: 'none' }}
              title="Limpar filtros" onClick={onLimpar}>Limpar</Button>
          </>
        )}
        {/* Mesmo tamanho dos filtros e sempre no fim da MESMA linha: no tamanho
            padrão ele não cabia ao lado da busca e caía sozinho para uma segunda
            linha, que custava 40 px de altura ao quadro (Lucas, 04/10/2026). */}
        <Button size="small" type="primary" icon={<PlusOutlined />}
          style={{ marginLeft: 'auto', flex: 'none' }} onClick={onNovoNegocio}>
          Novo Negócio
        </Button>
      </Flex>

    </>
  );
}
